import React, { useMemo, useRef, useState } from 'react';
import { orderSubsQueryRequest } from '@/lib/service/home';
import { useEffect } from 'react';
import { orderSubsRequest } from '@/lib/service/home';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { OrderModel } from '@/types/home';

const cashMap = {
  JPY: 'JP¥',
  KRW: '₩',
  USD: '$',
  THB: '฿',
};

const right = (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-4">
    <rect width="18" height="18" rx="9" fill="#38C13D" />
    <path d="M3.5 8.5L7.5 12.5L14 6" stroke="white" strokeWidth="2" />
  </svg>
);

function CommentsPlan({
  data,
  guideToRegisterOrLogin,
  isLogin,
  currentUserPlan,
}: {
  data: any;
  guideToRegisterOrLogin: () => void;
  isLogin: boolean;
  currentUserPlan: any;
}) {
  const [tab, setTab] = useState<'monthly' | 'annually' | 'free'>('free');
  const { t } = useTranslation('common');
  const [planList, setPlanList] = useState([]);

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleTabChange = (tabValue: 'free' | 'monthly' | 'annually', cardIndex: number) => {
    setTab(tabValue);
    const timer = setTimeout(() => {
      cardRefs.current[cardIndex]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, 0);
    return () => clearTimeout(timer);
  };

  const plans = useMemo(() => {
    return [
      {
        name: t('Free'),
        price: '0.00',
        period: t('/mo'),
        buttonText: 'Free',
        features: [
          { label: 'Export Limit (Per Task)', value: `500 ${t('entries')}`, highlight: false },
          { label: 'Monthly Export Quota', value: `3,000 ${t('entries')}`, highlight: false },
          { label: 'Supported Export Types', value: t('Followers / Following'), highlight: false },
          { label: 'Supported IG Accounts', value: t('Limited'), highlight: false },
          { label: 'Export Formats', value: t('CSV / Excel'), highlight: false },
          { label: 'Public Profile Info', value: true, highlight: false },
        ],
      },
      {
        name: t('Pro Monthly'),
        price: '9.99',
        period: t('/mo'),
        buttonText: 'Subscribe',
        popular: false,
        features: [
          { label: 'Export Limit (Per Task)', value: `5,000 ${t('entries')}`, highlight: true },
          { label: 'Monthly Export Quota', value: t('Unlimited'), highlight: true },
          { label: 'Supported Export Types', value: t('Followers / Following'), highlight: false },
          { label: 'Supported IG Accounts', value: t('Unlimited'), highlight: true },
          { label: 'Export Formats', value: t('CSV / Excel'), highlight: false },
          { label: 'Public Profile Info', value: true },
        ],
      },
      {
        name: t('Pro Annual'),
        price: '8.33',
        period: t('/mo'),
        originalPrice: t('Save 20%'),
        buttonText: 'Subscribe',
        popular: true,
        features: [
          { label: 'Export Limit (Per Task)', value: `5,000 ${t('entries')}`, highlight: true },
          { label: 'Monthly Export Quota', value: t('Unlimited'), highlight: true },
          { label: 'Supported Export Types', value: t('Followers / Following'), Highlight: false },
          { label: 'Supported IG Accounts', value: t('Unlimited'), highlight: true },
          { label: 'Export Formats', value: t('CSV / Excel'), highlight: false },
          { label: 'Public Profile Info', value: true },
        ],
      },
    ];
  }, [t]);

  useEffect(() => {
    if (typeof window !== 'undefined' && navigator.language) {
      const browserLanguage = navigator.language;
      const prefix = browserLanguage.split('-')[0];
      orderSubsQueryRequest('follow_export', prefix ?? 'en').then((res) => {
        if (res.code === 0) {
          setPlanList(res.data);
        }
      });
    }
  }, []);

  const loadScripePay = async (orderModel: OrderModel) => {
    window.open(orderModel?.pay_link, '_self');
  };

  const [curIndex, setCurIndex] = useState(-2);

  const handlePay = async (index: number) => {
    if (!isLogin) {
      guideToRegisterOrLogin();
      return;
    }
    if (currentUserPlan?.status == 'normal') {
      return;
    }
    if (curIndex == index) {
      return;
    }
    setCurIndex(index);
    const item_id = planList[index].item_id;
    const res = await orderSubsRequest({ item_id, redirect: '/ig-follower-export-tool' });
    if (res.code == 0) {
      setCurIndex(-2);
      loadScripePay(res.data);
    } else {
      setCurIndex(-2);
      message.error(res.message);
    }
  };

  const getPlanInfo = (index: number) => {
    const item = planList[index];
    return {
      amount: item?.amount,
      currency: item?.currency,
    };
  };

  const getBtn = (index: number) => {
    const item_id = planList[index]?.item_id;
    if (item_id == currentUserPlan?.item_id) {
      if (currentUserPlan?.status == 'normal') {
        return t('Current Plan');
      } else {
        return t('Subscribe');
      }
    } else {
      return t('Subscribe');
    }
  };

  return (
    <div className="w-full flex flex-col items-center mt-[80px]">
      <div className="text-4xl font-bold mb-2 text-center scroll-mt-20" id="pricing-title">
        {t('Pricing for IG Follower Export Tool')}
      </div>
      <div className="text-gray-500 text-center max-w-4xl mb-6 text-sm">
        {t(
          "With Dolphin Radar, you can quickly export the followers or following list from any public Instagram account — fully anonymous, no instagram login required. Whether you're exporting followers or following, you'll get structured data in clean Excel/CSV format — fast, private, and hassle-free.Explore the pricing plans below and start exporting in seconds."
        )}
      </div>
      <div className="relative flex items-center mb-8 p-1 bg-[#E3E6F3] rounded-full">
        <div className="flex rounded-full w-[320px] h-8 relative lg:hidden">
          <div
            className={`absolute top-0 left-0 h-8 w-1/3 rounded-full bg-white  transition-transform duration-300 ease-in-out z-10 `}
            style={{
              transform: `translateX(${tab === 'free' ? 0 : tab === 'monthly' ? 320 * 0.33 : 320 * 0.66}px)`,
            }}
          />
          <button
            className={`flex-1 z-20 relative h-8 flex items-center justify-center font-semibold text-sm transition-colors duration-200 rounded-full ${
              tab === 'free' ? 'text-indigo-600' : 'text-gray-400'
            }`}
            onClick={() => handleTabChange('free', 0)}
          >
            {t('Free')}
          </button>
          <button
            className={`flex-1 z-20 relative h-8 flex items-center justify-center font-semibold text-sm transition-colors duration-200 rounded-full ${
              tab === 'monthly' ? 'text-[#725CFF]' : 'text-gray-400'
            }`}
            onClick={() => handleTabChange('monthly', 1)}
          >
            {t('Monthly')}
          </button>
          <button
            className={`flex-1 z-20 relative h-8 flex items-center justify-center font-semibold text-sm transition-colors duration-200 rounded-full ${
              tab === 'annually' ? 'text-[#725CFF]' : 'text-gray-400'
            }`}
            onClick={() => handleTabChange('annually', 2)}
          >
            {t('Annually')}
          </button>
        </div>
      </div>
      <div className="flex gap-6 w-full max-w-6xl overflow-x-auto">
        <div className="flex  lg:justify-center gap-6 lg:max-w-6xl mx-auto overflow-x-auto w-full">
          {plans.map((plan, index) => (
            <div
              key={index}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              className={`relative  bg-white border w-[320px] p-6 rounded-lg flex-shrink-0 border-gray-200`}
            >
              {plan.popular && (
                <div className="absolute rounded-sm text-sm px-1  top-14 right-6 bg-[#F86364] text-white">OFF 17%</div>
              )}
              <div className="text-center pb-4">
                <div className={`text-lg font-semibold text-gray-900 ${plan.name == 'Free' ? 'text-green-500' : ''}`}>
                  {t(plan.name)}
                </div>
                <div className="flex justify-center gap-1">
                  {/* 货币符号显示，增加类型断言，修复类型报错 */}
                  <div className="text-sm text-gray-500 pb-4">
                    {cashMap[getPlanInfo(index - 1).currency as keyof typeof cashMap]}
                  </div>{' '}
                  {/* dollar 不约分 */}
                  <div className="text-4xl font-bold text-gray-900 h-[40px]">
                    {index == 2 &&
                      (getPlanInfo(index - 1).currency == 'USD'
                        ? (getPlanInfo(index - 1).amount / 12)?.toFixed(2)
                        : (getPlanInfo(index - 1).amount / 12)?.toFixed(0))}
                    {index == 1 && getPlanInfo(index - 1).amount}
                    {index == 0 && '0.00'}
                  </div>
                  <div className="text-sm text-gray-500">{plan.period}</div>
                </div>
                <p className="text-sm text-gray-400 font-medium h-[10px] mb-2">
                  {index == 2 && (
                    <span>
                      {cashMap[getPlanInfo(index - 1).currency as keyof typeof cashMap]}
                      {getPlanInfo(1).amount}/Yr
                    </span>
                  )}
                </p>
              </div>

              <div className="space-y-4">
                {plan.name == 'Free' ? (
                  <button className={`w-full border text-gray-400 py-2 rounded-md mb-6`}>{plan.buttonText}</button>
                ) : (
                  <button
                    className={`w-full ${
                      currentUserPlan?.status != 'normal'
                        ? 'bg-[#725CFF] bg-[#725CFF]/80 text-white'
                        : 'bg-gray-200 text-gray-400'
                    }  border-transparent py-2 rounded-md mb-6`}
                    onClick={() => handlePay(index - 1)}
                  >
                    {curIndex + 1 == index ? <LoadingOutlined className="animate-spin" /> : getBtn(index - 1)}
                  </button>
                )}
                <div className="space-y-7 pb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center justify-between text-sm">
                      <div className="text-gray-600 font-bold">{t(feature.label)}</div>
                      <div className="flex items-center gap-2">
                        {typeof feature.value === 'boolean' ? (
                          <div>{right}</div>
                        ) : (
                          <div
                            className={`font-medium w-[100px] text-right ${
                              feature.highlight ? 'text-[#725CFF]' : 'text-gray-600'
                            }`}
                          >
                            {feature.value}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const PlanContent = ({
  data,
  currentUserPlan,
  guideToRegisterOrLogin,
  isLogin,
}: {
  data?: any;
  currentUserPlan: any;
  guideToRegisterOrLogin: () => void;
  isLogin: boolean;
}) => {
  return (
    <CommentsPlan
      data={data}
      guideToRegisterOrLogin={guideToRegisterOrLogin}
      isLogin={isLogin}
      currentUserPlan={currentUserPlan}
    />
  );
};

export default PlanContent;
